import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "../../api/errors";
import { resendVerificationEmail } from "../api/emailVerification";
import { useHostToast } from "../../host/useHostToast";

interface UseResendVerificationEmail {
  /** A request is in flight — drives the button spinner + double-submit guard. */
  pending: boolean;
  /** A verification email was sent this session — switches the badge copy from
   *  the initial prompt to the "sent / resend" state. Session-only (resets on
   *  remount), mirroring the presenter's non-persisted flag. */
  hasRequested: boolean;
  resend: () => Promise<void>;
}

// The backend forwards general-api's 429 rate-limit body verbatim under
// `upstream` (see general-api.errors.ts), so the real message ("You have
// requested too many verification emails…") is at body.upstream.message. Pull
// it out so the toast shows the actual reason instead of a generic error.
function upstreamMessage(err: unknown): string | null {
  if (err instanceof ApiError && err.body && typeof err.body === "object") {
    const upstream = (err.body as { upstream?: { message?: unknown } }).upstream;
    if (upstream && typeof upstream.message === "string") return upstream.message;
  }
  return null;
}

// AHA-39565 — owns the resend action for the current user's own badge. Modeled
// on useRemoveMember: pending flag, toast on both outcomes, tracking mirrors the
// presenter's email-verification events.
export function useResendVerificationEmail(): UseResendVerificationEmail {
  const { t } = useTranslation();
  const toast = useHostToast();
  const [pending, setPending] = useState(false);
  const [hasRequested, setHasRequested] = useState(false);

  // Analytics for the resend/verify action is emitted on the button click via
  // <TrackOnClick> (click_email_verification_resend / _verify_now), matching the
  // presenter's v-aha-click events so they unify into one Mixpanel event. The
  // hook stays analytics-free and only owns the request + toast + UI state.
  const resend = useCallback(async () => {
    if (pending) return;
    setPending(true);
    try {
      const result = await resendVerificationEmail();
      if (result.alreadyVerified) {
        // Rare staleness: the row still reads unverified but the current email
        // is already verified upstream. Say so — and don't flip to the "sent"
        // copy, which would be misleading (nothing was sent).
        toast.success(t("team.emailVerification.alreadyVerified"));
      } else {
        toast.success(t("team.emailVerification.sentToast"));
        setHasRequested(true);
      }
    } catch (err) {
      toast.error(upstreamMessage(err) ?? t("team.emailVerification.error"));
    } finally {
      setPending(false);
    }
  }, [pending, t, toast]);

  return { pending, hasRequested, resend };
}
